import {
  Provider as TooltipProvider,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'components/Tooltip'
import { TooltipTimestamp } from 'features/timeSeries/components/TooltipTimestamp'
import { useCursorData } from 'features/timeSeries/CursorDataContext'
import { formatCurrentValue } from 'features/timeSeries/utils/scatterPlotTableHelpers'
import React, { useMemo } from 'react'
import styled from 'styled-components'
import { SensorScatterplotData } from 'types'

const TooltipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
  &:last-child {
    margin-bottom: 0;
  }
`

const ColorDot = styled.div<{ color: string }>`
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background-color: ${({ color }) => color};
`

const ValueText = styled.span`
  font-weight: 600;
`

const TooltipAnchor = styled.div.attrs<{ $left: number; $top: number }>(
  ({ $left, $top }) => ({
    style: {
      left: `${$left}px`,
      top: `${$top}px`,
    },
  })
)<{ $left: number; $top: number }>`
  position: fixed;
  width: 1px;
  height: 1px;
  pointer-events: none;
  visibility: hidden;
`

interface InterpolatedValueTooltipProps {
  cursorPosition: {
    x: number
    y: number
    clientX: number
    clientY: number
  } | null
  tableData: SensorScatterplotData[]
  sensorVisibilityConfig: Record<string, boolean>
}

export const InterpolatedValueTooltip: React.FC<
  InterpolatedValueTooltipProps
> = ({ cursorPosition, tableData, sensorVisibilityConfig }) => {
  const { cursorData } = useCursorData()

  const shouldShow = useMemo(
    () => cursorPosition && Object.keys(cursorData).length > 0,
    [cursorPosition, cursorData]
  )

  if (!shouldShow || !cursorPosition) {
    return null
  }

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip open={true}>
        <TooltipTrigger asChild>
          <TooltipAnchor
            $left={cursorPosition.clientX}
            $top={cursorPosition.clientY}
          />
        </TooltipTrigger>
        <TooltipContent side='right' align='start' sideOffset={15}>
          <TooltipTimestamp tableData={tableData} />
          {tableData.map((item) => {
            if (sensorVisibilityConfig[item.id] === false) return null

            const [valStr, unitStr] = formatCurrentValue(
              item.currentValue,
              item.unit
            )

            return (
              <TooltipRow key={item.id}>
                <ColorDot color={item.color} />
                <span>{item.name}:</span>
                <ValueText>
                  {valStr} {unitStr}
                </ValueText>
              </TooltipRow>
            )
          })}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
